import { minutesToHours } from '@/helpers/date-format';
import { mapTimeOffRequestFromDTO } from '@/modules/time-off/mappers/time-off-request.mapper';

import { calculateProgressPercentage } from '../helpers/progress-percentage.helper';
import {
  BookedMinutesBreakdownResponse,
  MonthCalendarDayResponse,
  MonthViewResponse,
  TrackedMinutesBreakdownResponse,
  WeekDayBreakdownResponse,
  WeekDayProjectBreakdownResponse,
  WeekViewProjectBreakdownResponse,
  WeekViewResponse,
  WorklogsSummaryResponse,
} from '../types/dto/worklogs-summary.dto';
import { TimeOffRequestSummaryStatusDTO } from '../types/dto/worklogs-time-off-summary.dto';
import {
  BookedMinutesBreakdown,
  DayOverview,
  MonthCalendarDay,
  MonthOverview,
  ProjectBreakdown,
  ProjectTimeTrackingSummary,
  TrackedBreakdown,
  WeekDayBreakdown,
  WeekDayProjectBreakdown,
  WeeklySummary,
} from '../types/time-tracking';
import { mapTimeOffRequestSummaryStatusFromDTO } from './time-off-summary.mapper';

const mapBookedBreakdown = (items: BookedMinutesBreakdownResponse[]): BookedMinutesBreakdown[] =>
  items.map(item => ({
    bookedHours: minutesToHours(item.minutes),
    isBackgroundWork: item.isBackgroundWork,
  }));

const mapTrackedBreakdown = (items: TrackedMinutesBreakdownResponse[]): TrackedBreakdown[] =>
  items.map(item => ({
    trackedHours: minutesToHours(item.minutes),
    isBackgroundWork: item.isBackgroundWork,
  }));

const mapProjectBreakdown = (item: WeekDayProjectBreakdownResponse): ProjectBreakdown => ({
  projectId: item.project.projectId,
  projectName: item.project.projectName,
  colorHex: item.project.colorHex,
  logoUrl: item.project.logoUrl,
  trackedHours: minutesToHours(item.totalTrackedMinutes),
  trackedBreakdown: mapTrackedBreakdown(item.tracked),
  bookedHours: minutesToHours(item.effectiveBookedMinutes),
  bookedBreakdown: mapBookedBreakdown(item.bookedBreakdown),
});

const mapWeekViewProjectBreakdown = (
  item: WeekViewProjectBreakdownResponse
): WeekDayProjectBreakdown => ({
  ...mapProjectBreakdown(item),
  isBackgroundWork: item.isBackgroundWork,
});

const mapDayBreakdown = (day: WeekDayBreakdownResponse): WeekDayBreakdown => ({
  date: day.date,
  dailyCapacityHours: minutesToHours(day.effectiveCapacityMinutes),
  dailyTotalCapacityHours: minutesToHours(day.totalCapacityMinutes),
  trackedHours: minutesToHours(day.totalTrackedMinutes),
  trackedBreakdown: mapTrackedBreakdown(day.tracked),
  bookedHours: minutesToHours(day.effectiveBookedMinutes),
  bookedBreakdown: mapBookedBreakdown(day.bookedBreakdown),
  projectBreakdown: day.projectBreakdown.map(mapWeekViewProjectBreakdown),
  timeOff: day.timeOff.map(item => ({
    status: mapTimeOffRequestSummaryStatusFromDTO(item.status),
    totalHours: minutesToHours(item.totalMinutes),
  })),
});

const mapAggregatedProject = (item: {
  project: WeekDayProjectBreakdownResponse['project'];
  totalTrackedMinutes: number;
  tracked: TrackedMinutesBreakdownResponse[];
  totalBookedMinutes: number;
  effectiveBookedMinutes: number;
  bookedBreakdown: BookedMinutesBreakdownResponse[];
}): ProjectTimeTrackingSummary => {
  const trackedHours = minutesToHours(item.totalTrackedMinutes);
  const bookedHours = minutesToHours(item.effectiveBookedMinutes);
  return {
    project: item.project,
    trackedHours,
    trackedBreakdown: mapTrackedBreakdown(item.tracked),
    bookedHours,
    progressPercentage: calculateProgressPercentage(trackedHours, bookedHours),
    bookedBreakdown: mapBookedBreakdown(item.bookedBreakdown),
  };
};

export const mapWeekOverviewFromResponse = (
  response: WeekViewResponse
): { summary: WeeklySummary; dailyBreakdown: WeekDayBreakdown[] } => {
  const totalTrackedHours = minutesToHours(response.totalTrackedMinutes);
  const totalBookedHours = minutesToHours(response.effectiveBookedMinutes);
  const totalCapacityHours = minutesToHours(response.totalCapacityMinutes);
  const effectiveCapacityHours = minutesToHours(response.effectiveCapacityMinutes);

  const summary: WeeklySummary = {
    weekStartDate: response.startDate,
    weekEndDate: response.endDate,
    totalTrackedHours,
    totalBookedHours,
    totalCapacityHours,
    effectiveCapacityHours,
    timeOffSummary: response.timeOffSummary.map(item => ({
      status: mapTimeOffRequestSummaryStatusFromDTO(item.status),
      totalHours: minutesToHours(item.totalMinutes),
    })),
    timeOffRequests: response.timeOffRequests.map(mapTimeOffRequestFromDTO),
    trackedBreakdown: mapTrackedBreakdown(response.tracked),
    progressPercentage: calculateProgressPercentage(totalTrackedHours, totalBookedHours),
    projectSummaries: response.projects.map(mapAggregatedProject),
    bookedBreakdown: mapBookedBreakdown(response.bookedBreakdown),
  };

  return {
    summary,
    dailyBreakdown: response.dailyBreakdown.map(mapDayBreakdown),
  };
};

const mapMonthCalendarDay = (day: MonthCalendarDayResponse): MonthCalendarDay => {
  const totalTimeOffMinutes = day.timeOffSummary.reduce((sum, item) => sum + item.totalMinutes, 0);
  const isFullDayTimeOff =
    day.totalCapacityMinutes > 0 && totalTimeOffMinutes >= day.totalCapacityMinutes;

  return {
    date: day.date,
    trackedHours: minutesToHours(day.totalTrackedMinutes),
    totalCapacityHours: minutesToHours(day.totalCapacityMinutes),
    effectiveCapacityHours: minutesToHours(day.effectiveCapacityMinutes),
    topProjects: day.topProjects.map(p => ({
      projectId: p.projectId,
      colorHex: p.colorHex,
    })),
    timeOff: day.timeOffSummary.map(item => ({
      status: mapTimeOffRequestSummaryStatusFromDTO(item.status),
      totalHours: minutesToHours(item.totalMinutes),
    })),
    isFullDayTimeOff,
  };
};

export const mapMonthOverviewFromResponse = (response: MonthViewResponse): MonthOverview => ({
  monthStartDate: response.startDate,
  monthEndDate: response.endDate,
  days: response.days.map(mapMonthCalendarDay),
  monthlyProjects: response.projects.map(mapAggregatedProject),
  timeOffRequests: response.timeOffRequests.map(mapTimeOffRequestFromDTO),
});

export const mapDayOverviewFromResponse = (
  response: WorklogsSummaryResponse,
  date: string
): DayOverview => {
  const approvedMinutes =
    response.timeOffSummary.find(item => item.status === TimeOffRequestSummaryStatusDTO.APPROVED)
      ?.totalMinutes ?? 0;
  const pendingMinutes =
    response.timeOffSummary.find(item => item.status === TimeOffRequestSummaryStatusDTO.PENDING)
      ?.totalMinutes ?? 0;
  const totalTimeOffMinutes = approvedMinutes + pendingMinutes;

  return {
    date,
    trackedHours: minutesToHours(response.totalTrackedMinutes),
    trackedBreakdown: mapTrackedBreakdown(response.tracked),
    bookedHours: minutesToHours(response.effectiveBookedMinutes),
    bookedBreakdown: mapBookedBreakdown(response.bookedBreakdown),
    capacityHours: minutesToHours(response.totalCapacityMinutes),
    effectiveCapacityHours: minutesToHours(response.effectiveCapacityMinutes),
    timeOffApprovedHours: minutesToHours(approvedMinutes),
    timeOffPendingHours: minutesToHours(pendingMinutes),
    isFullDayTimeOff:
      response.totalCapacityMinutes > 0 && totalTimeOffMinutes >= response.totalCapacityMinutes,
    timeOffStatus:
      pendingMinutes > 0
        ? mapTimeOffRequestSummaryStatusFromDTO(TimeOffRequestSummaryStatusDTO.PENDING)
        : approvedMinutes > 0
          ? mapTimeOffRequestSummaryStatusFromDTO(TimeOffRequestSummaryStatusDTO.APPROVED)
          : undefined,
    timeOff: response.timeOffSummary.map(item => ({
      status: mapTimeOffRequestSummaryStatusFromDTO(item.status),
      totalHours: minutesToHours(item.totalMinutes),
    })),
    projectBreakdown: response.projectSummaries.map(mapProjectBreakdown),
  };
};
